import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  X,
  AlertTriangle,
  Calendar,
  DollarSign,
  FileText,
  Building,
  Users,
  Zap,
  Clock,
  CheckCircle
} from 'lucide-react';
import { format } from 'date-fns';

interface UrgentPaymentsViewProps {
  onClose: () => void;
  customerFilter: string[];
  accountFilter: string[];
  commodityFilter: string[];
}

interface UrgentBill {
  id: string;
  bill_id: string;
  customer_name: string;
  vendor_name: string;
  account_number: string;
  commodity: string;
  due_date: string;
  amount_due: number;
  days_overdue: number;
  urgency: 'due-today' | 'overdue' | 'service-risk';
}

export function UrgentPaymentsView({
  onClose,
  customerFilter,
  accountFilter,
  commodityFilter,
}: UrgentPaymentsViewProps) {
  // Generate dummy urgent bills
  const urgentBills: UrgentBill[] = [
    {
      id: '1',
      bill_id: '1107769',
      customer_name: 'Customer A',
      vendor_name: 'Duke Energy',
      account_number: '9101 2834 0913',
      commodity: 'electric',
      due_date: '2025-12-16',
      amount_due: 1250.50,
      days_overdue: 0,
      urgency: 'due-today',
    },
    {
      id: '2',
      bill_id: '1108315',
      customer_name: 'Customer B',
      vendor_name: 'PG&E',
      account_number: '9101 2835 6292',
      commodity: 'gas',
      due_date: '2025-12-14',
      amount_due: 2450.75,
      days_overdue: 2,
      urgency: 'overdue',
    },
    {
      id: '3',
      bill_id: '1110721',
      customer_name: 'Customer C',
      vendor_name: 'Southern California Edison',
      account_number: '9101 5438 6734',
      commodity: 'electric',
      due_date: '2025-12-10',
      amount_due: 3890.25,
      days_overdue: 6,
      urgency: 'service-risk',
    },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'MM/dd/yyyy');
  };

  const getUrgencyBadge = (urgency: UrgentBill['urgency']) => {
    const variants = {
      'due-today': { variant: 'default' as const, label: 'Due Today', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
      'overdue': { variant: 'destructive' as const, label: 'Overdue', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
      'service-risk': { variant: 'destructive' as const, label: 'Service Risk', icon: AlertTriangle, color: 'text-red-700', bg: 'bg-red-100' },
    };
    
    const config = variants[urgency];
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className={`gap-1.5 ${config.bg}`}>
        <Icon className={`h-3 w-3 ${config.color}`} />
        {config.label}
      </Badge>
    );
  };

  const totalAmount = urgentBills.reduce((sum, bill) => sum + bill.amount_due, 0);

  return (
    <Card className="border-l-4 border-l-red-500 bg-red-50/50 dark:bg-red-950/10">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <CardTitle className="text-lg text-red-900 dark:text-red-100">Urgent Payments</CardTitle>
              <CardDescription>High-priority bills requiring immediate attention</CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary */}
        <div className="bg-background rounded-lg p-4 border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Urgent Bills</p>
              <p className="text-2xl font-bold">{urgentBills.length}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total Amount</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(totalAmount)}</p>
            </div>
          </div>
        </div>

        {/* Urgent Bills Table */}
        <div className="overflow-x-auto border rounded-lg">
          <Table>
            <TableHeader className="bg-muted/30">
              <TableRow>
                <TableHead>
                  <div className="flex items-center gap-2 font-semibold">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    Bill ID
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2 font-semibold">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    Customer
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2 font-semibold">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    Vendor
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2 font-semibold">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    Account
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2 font-semibold">
                    <Zap className="h-4 w-4 text-muted-foreground" />
                    Commodity
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2 font-semibold">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Due Date
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2 font-semibold">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    Amount Due
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2 font-semibold">
                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                    Urgency
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2 font-semibold">
                    <CheckCircle className="h-4 w-4 text-muted-foreground" />
                    Actions
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {urgentBills.map((bill, index) => (
                <TableRow key={bill.id} className={index % 2 === 0 ? 'bg-muted/30' : ''}>
                  <TableCell>
                    <span className="font-mono font-medium">{bill.bill_id}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Users className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-medium">{bill.customer_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{bill.vendor_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm">{bill.account_number}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {bill.commodity}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm">{formatDate(bill.due_date)}</span>
                      {bill.days_overdue > 0 && (
                        <span className="text-xs text-red-600">({bill.days_overdue}d overdue)</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-semibold">{formatCurrency(bill.amount_due)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getUrgencyBadge(bill.urgency)}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" className="gap-2">
                      <CheckCircle className="h-3 w-3" />
                      Create Payment Task
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

